import { supabase } from './supabaseClient';

const TABLE = 'coco_republic_orders_v2';

const OPERATION_TYPES = ['Create', 'Scheduled', 'Complete', 'Failed', 'SAP_SO', 'Exchange', 'Pickup'];

/** Operations that are pulled directly from SQL for review (no Storis/SAP result) */
const REVIEW_OPERATIONS = new Set(['Exchange', 'Pickup']);
export { REVIEW_OPERATIONS };

/**
 * Get the result field name for a given operation type.
 * SAP_SO uses sap_result, all others use storis_result.
 */
function getResultField(operation) {
  return operation === 'SAP_SO' ? 'sap_result' : 'storis_result';
}

/**
 * Compute ISO date string for "X days ago" from today (start of day UTC).
 */
function daysAgoISO(days) {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString();
}

/** Date range presets: value is number of days back (0 = today only) */
const DATE_RANGE_OPTIONS = [
  { value: 0, label: 'Today' },
  { value: 1, label: 'Last 1 day' },
  { value: 3, label: 'Last 3 days' },
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 30, label: 'Last 30 days' },
  { value: -1, label: 'All time' },
];

/**
 * Fetch deliveries filtered by operation type, date range, and optional search.
 */
export async function fetchDeliveries({ operation, search, daysBack = 0 } = {}) {
  let query = supabase
    .from(TABLE)
    .select(`
      *,
      action_notes!delivery_v2_id (
        id,
        note_text,
        created_by,
        created_at
      )
    `)
    .order('azure_timestamp', { ascending: false });

  if (operation) {
    query = query.eq('operation', operation);
  }

  // Date filter on azure_timestamp
  if (daysBack >= 0) {
    query = query.gte('azure_timestamp', daysAgoISO(daysBack));
  }

  if (search) {
    query = query.or(
      `name.ilike.%${search}%,customer_reference.ilike.%${search}%,customer_no.ilike.%${search}%`
    );
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching deliveries:', error);
    throw error;
  }

  return data.map(transformDelivery);
}

/**
 * Get a single delivery by ID
 */
export async function getDeliveryById(id) {
  const { data, error } = await supabase
    .from(TABLE)
    .select(`
      *,
      action_notes!delivery_v2_id (
        id,
        note_text,
        created_by,
        created_at
      )
    `)
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching delivery:', error);
    throw error;
  }

  return transformDelivery(data);
}

/**
 * Mark a delivery as actioned
 */
export async function markDeliveryAsActioned(id, userId) {
  const { data, error } = await supabase
    .from(TABLE)
    .update({
      is_actioned: true,
      actioned_by: userId,
    })
    .eq('id', id)
    .select();

  if (error) {
    console.error('Error marking delivery as actioned:', error);
    throw error;
  }

  await addActionNote(id, 'Marked as actioned', userId);

  return data.map(transformDelivery);
}

/**
 * Add an action note to a delivery
 */
export async function addActionNote(deliveryId, noteText, createdBy = 'System') {
  const { data, error } = await supabase
    .from('action_notes')
    .insert({
      delivery_v2_id: deliveryId,
      note_text: noteText,
      created_by: createdBy,
    })
    .select()
    .single();

  if (error) {
    console.error('Error adding action note:', error);
    throw error;
  }

  return {
    id: data.id,
    text: data.note_text,
    createdBy: data.created_by,
    timestamp: data.created_at,
  };
}

/**
 * Get status counts for a given operation type.
 * Returns total, needsAction, completed, actioned counts.
 */
export async function getStatusCounts(operation, daysBack = 0) {
  const isReviewOp = REVIEW_OPERATIONS.has(operation);
  const resultField = getResultField(operation);
  const selectFields = isReviewOp ? 'id, sap_result, is_actioned' : `id, ${resultField}, is_actioned`;

  let query = supabase.from(TABLE).select(selectFields);

  if (operation) {
    query = query.eq('operation', operation);
  }

  if (daysBack >= 0) {
    query = query.gte('azure_timestamp', daysAgoISO(daysBack));
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching status counts:', error);
    throw error;
  }

  const total = data.length;
  const actioned = data.filter((d) => d.is_actioned).length;

  if (isReviewOp) {
    // Exchange/Pickup: hybrid legacy + auto-SO logic.
    // - sap_result null      => legacy / pending: needs operator until actioned
    // - sap_result 'Success' => auto-SO succeeded: counted as completed
    // - sap_result 'Failed'  => auto-SO failed: needs operator until actioned
    const needsAction = data.filter(
      (d) => !d.is_actioned && (d.sap_result == null || d.sap_result === 'Failed')
    ).length;
    const completed = total - needsAction;
    return { total, needsAction, completed, actioned };
  }

  const needsAction = data.filter(
    (d) => d[resultField] === 'Failed' && !d.is_actioned
  ).length;
  const completed = total - needsAction;

  return { total, needsAction, completed, actioned };
}

/**
 * Transform database record to frontend format (snake_case to camelCase)
 */
function transformDelivery(record) {
  if (!record) return null;

  // Parse items JSONB - could be array, string, or null
  let items = [];
  if (record.items) {
    try {
      items = typeof record.items === 'string' ? JSON.parse(record.items) : record.items;
    } catch {
      items = [];
    }
  }

  return {
    id: record.id,
    partitionKey: record.partition_key,
    rowKey: record.row_key,
    operation: record.operation,
    storisResult: record.storis_result,
    sapResult: record.sap_result,
    azureTimestamp: record.azure_timestamp,
    name: record.name,
    customerNo: record.customer_no,
    customerReference: record.customer_reference,
    suburb: record.suburb,
    state: record.state,
    postCode: record.post_code,
    street: record.street,
    streetNo: record.street_no,
    unitNo: record.unit_no,
    mobile: record.mobile,
    email: record.email,
    docType: record.doc_type,
    facility: record.facility,
    reqDate: record.req_date_h,
    deliveryType: record.delivery_type,
    installation: record.installation,
    deliveryNotes: record.delivery_notes,
    directionNotes: record.direction_notes,
    isActioned: record.is_actioned,
    actionedBy: record.actioned_by,
    payload: record.payload,
    returnData: record.return_data,
    orderNumber: record.order_number,
    sourceReference: record.source_reference,
    sapSoNumber: record.sap_so_number,
    pickupSapSoNumber: record.pickup_sap_so_number,
    items,
    actionNotes: (record.action_notes || [])
      .map((note) => ({
        id: note.id,
        text: note.note_text,
        createdBy: note.created_by,
        timestamp: note.created_at,
      }))
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
  };
}

/**
 * Fetch Logic App payload for a delivery (calls worker endpoint, uses Supabase cache)
 */
export async function fetchLogicAppPayload(deliveryId) {
  const res = await fetch(`/api/deliveries/${deliveryId}/logic-app-payload`);
  const result = await res.json();
  if (!res.ok) throw new Error(result.error || 'Failed to fetch Logic App payload');
  return result.data;
}

/**
 * Call the external Azure Function to set SAP SO Number(s) for a delivery.
 * Posts the OrderId (row_key) and the manually entered SO Number(s).
 *
 * @param {Object} delivery
 * @param {{ sapSoNumber?: string, pickupSapSoNumber?: string }} numbers
 *   For SAP_SO and Pickup, supply only `sapSoNumber`.
 *   For Exchange, supply whichever leg(s) the operator filled in;
 *   the function POSTs PickupSAPSONumber alongside SAPSONumber so the
 *   server can update both columns.
 */
export async function setSoNumber(delivery, numbers) {
  const endpoint = import.meta.env.VITE_SAP_SO_FUNCTION_URL;
  if (!endpoint) {
    throw new Error('SAP SO Function URL is not configured (VITE_SAP_SO_FUNCTION_URL)');
  }

  const body = {
    OrderId: parseInt(delivery.rowKey, 10),
    SAPSONumber: numbers.sapSoNumber,
  };
  if (numbers.pickupSapSoNumber != null) {
    body.PickupSAPSONumber = numbers.pickupSapSoNumber;
  }

  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const result = await res.json();
  if (!res.ok) {
    throw new Error(result.error || result.message || 'Failed to set SO Number');
  }

  // Mirror locally so the change is visible immediately. Update whichever
  // columns the caller actually supplied — never overwrite a populated leg
  // with null.
  if (delivery.rowKey) {
    const updates = {};
    if (numbers.sapSoNumber != null) updates.sap_so_number = numbers.sapSoNumber;
    if (numbers.pickupSapSoNumber != null) updates.pickup_sap_so_number = numbers.pickupSapSoNumber;

    if (Object.keys(updates).length > 0) {
      const { error: updateError } = await supabase
        .from(TABLE)
        .update(updates)
        .eq('row_key', delivery.rowKey);

      if (updateError) {
        console.error('Failed to update local SO numbers:', updateError);
      }
    }
  }

  return result;
}

export { OPERATION_TYPES, DATE_RANGE_OPTIONS, getResultField };
